type EventCallback = (data: any) => void;

/**
 * Class to handle event-based communication between different parts of an application.
 */
class EventBus {
  private listeners: Record<string, EventCallback[]>;

  constructor() {
    this.listeners = {};
  }

  /**
   * Subscribes to an event with a callback to be executed when the event is published.
   * @param event The event name to subscribe to.
   * @param callback The callback function that is executed when the event is published.
   * @example
   * bus.subscribe('dataLoaded', (data) => {
   *   console.log('Received:', data);
   * });
   */
  subscribe(event: string, callback: EventCallback): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Unsubscribes from an event.
   * @param event The event name to unsubscribe from.
   * @param callback The callback function to remove from the subscription list.
   * @example
   * const handler = data => console.log('Handled:', data);
   * bus.subscribe('dataLoaded', handler);
   * bus.unsubscribe('dataLoaded', handler);
   */
  unsubscribe(event: string, callback: EventCallback): void {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(listener => listener !== callback);
    }
  }

  /**
   * Publishes an event with the given data to all subscribed callbacks.
   * @param event The event name to publish.
   * @param data The data to pass to the callbacks.
   * @example
   * bus.publish('dataLoaded', { id: 123, message: 'Hello world' });
   */
  publish(event: string, data: any): void {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
}

export default new EventBus();

// Create a global event bus instance
// const bus = new EventBus();

// Example usage:
// bus.subscribe('dataLoaded', data => {
//   console.log('Data loaded:', data);
// });

// To publish an event with data:
// bus.publish('dataLoaded', { id: 1, content: 'This is an example data object.' });

// To unsubscribe a previously subscribed event:
// const specificHandler = (data: any) => console.log('Specific data:', data);
// bus.subscribe('dataLoaded', specificHandler);
// bus.unsubscribe('dataLoaded', specificHandler);
